import {
  Container,
  Box,
  Grid,
  Pagination,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { React, useState, useEffect } from "react";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import SoilTestLabCard from "../../components/Cards/SoilTestLabCard";
import Autocomplete from "@mui/material/Autocomplete";
import { GET_SOIL_LABS } from "../../globals/endpoints";
import api from "../../globals/axios";
import FarmAppBar from "../../components/Custom/FarmAppBar";

const SoilLabsLanding = () => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const [labs, setLabs] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [soilLab, setSoilLabs] = useState([]);
  const [selectedSoilLab, setSelectedSoilLab] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 0,
    total_objects: 0,
  });

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await api.get(`${GET_SOIL_LABS}/states`);
        const data = response.data;
        setStates(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStates();
  }, []);

  const handleStateChange = async (_, newValue) => {
    if (newValue) {
      setSelectedState(newValue);
    } else {
      setSelectedState(null);
      setDistricts([]);
      setSoilLabs([]);
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSelectedDistrict(null);
    setSelectedSoilLab(null);
  };

  const handleDistrictChange = (_, newValue) => {
    if (newValue) {
      setSelectedDistrict(newValue);
    } else {
      setSelectedDistrict(null);
      setSoilLabs([]);
    }
    setSelectedSoilLab(null);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const fetchLabs = async (page = 1, state, district, soil_lab) => {
    try {
      // fetch a specific soil lab
      if (state && district && soil_lab) {
        const { data } = await api.get(`${GET_SOIL_LABS}/${soil_lab.id}`);
        return { results: [data] };
      }

      //to fetch list of soil labs when state and district are selected
      else if (state && district) {
        const response = await api.get(
          `${GET_SOIL_LABS}?state=${encodeURIComponent(
            state
          )}&district=${encodeURIComponent(district)}&page=${page}`
        );
        const { data } = await api.get(
          `${GET_SOIL_LABS}?district=${encodeURIComponent(
            district
          )}&state=${encodeURIComponent(state)}`
        );
        setSoilLabs(data.results);
        return response.data;
      }
      // fetch soil labs and list of all districts when only state is selected
      else if (state) {
        const response = await api.get(
          `${GET_SOIL_LABS}?state=${encodeURIComponent(state)}&page=${page}`
        );
        const res = await api.get(
          `${GET_SOIL_LABS}/districts?state=${encodeURIComponent(state)}`
        );
        setDistricts(res.data);
        setSelectedDistrict(null);
        setSelectedSoilLab(null);
        return response.data;
      } else {
        const response = await api.get(`${GET_SOIL_LABS}?page=${page}`);
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching labs:", error);
      throw error;
    }
  };

  useEffect(() => {
    const getLabs = async (state, district, soil_lab) => {
      try {
        setLoading(true);
        const labsData = await fetchLabs(
          pagination.page,
          state,
          district,
          soil_lab
        );
        setLabs(labsData.results);
        setPagination((prev) => ({
          ...prev,
          total_pages: labsData.total_pages,
          total_objects: labsData.total_objects,
        }));
      } catch (error) {
        console.error("Failed to fetch labs:", error);
      } finally {
        setLoading(false);
      }
    };
    getLabs(selectedState, selectedDistrict, selectedSoilLab);
  }, [pagination.page, selectedState, selectedDistrict, selectedSoilLab]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPagination((prev) => ({
      ...prev,
      page: value,
    }));
  };

  return (
    <>
      <FarmAppBar />
      <Container maxWidth="md">
        <CustomTitle>{t("Soil Testing Labs")}</CustomTitle>
        <Box sx={{ mt: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                disablePortal
                id="state-select"
                options={states}
                value={selectedState}
                renderInput={(params) => (
                  <TextField {...params} label={t("Select State")} />
                )}
                onChange={handleStateChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Autocomplete
                disablePortal
                id="district-select"
                options={districts}
                value={selectedDistrict}
                renderInput={(params) => (
                  <TextField {...params} label={t("Select District")} />
                )}
                onChange={handleDistrictChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Autocomplete
                disablePortal
                id="soil-lab-select"
                options={soilLab}
                getOptionLabel={(option) => option.name || ""}
                value={selectedSoilLab}
                renderInput={(params) => (
                  <TextField {...params} label={t("Select Soil Lab")} />
                )}
                onChange={(event, newValue) => {
                  setSelectedSoilLab(newValue || "");
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
      <Container maxWidth="md">
        <LoaderContainer loading={loading} />
        <Grid container spacing={1} mt={1}>
          {labs.map((lab, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <SoilTestLabCard key={index} lab={lab} />
            </Grid>
          ))}
        </Grid>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
          }}
        >
          <Pagination
            count={pagination.total_pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            size={isSmallScreen ? "small" : "medium"}
            disabled={loading}
            sx={{ mb: "1rem" }}
          />
        </Box>
      </Container>
    </>
  );
};

export default SoilLabsLanding;
